import { normalizedUuidSchema } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useOrderDetailQuery, useProductionOrderSheetQuery } from '@/entities/order';
import { useQuoteDesignViewQuery, useQuoteDetailQuery } from '@/entities/quote';
import { PRODUCTION_ORDER_PRINTED_MESSAGE } from '@/features/order/production-order-sheet';
import { koreanDate, orderErrorCode } from '@/shared/lib';
import { Button, EmptyState } from '@/shared/ui';
import {
  ProductionOrderSheetDocument,
  ProductionOrderSheetFaces,
} from '@/widgets/production-order-sheet';

const THUMBNAIL_WAIT_MS = 3000;
const FACES_WAIT_MS = 15000;

/**
 * 발주서 인쇄 전용 화면 — 관리자 머리글·사이드바 없이 발주서 문서만 A4 로 찍는다.
 * 편집한 면을 그릴 수 있으면(견적 상세 + 디자인 뷰 READY) 모든 면을 그린 뒤(최대 15초), 그릴 수 없으면 앞면 썸네일을 불러온 뒤(최대 3초)
 * 인쇄창을 한 번만 연다(도구줄의 다시 인쇄로 다시 열 수 있다).
 */
export function ProductionOrderPrintPage(): ReactElement {
  const { orderId } = useParams<{ orderId: string }>();
  const parsed = normalizedUuidSchema.safeParse(orderId);
  const id = parsed.success ? parsed.data : '';
  const orderQuery = useOrderDetailQuery(id, { enabled: parsed.success });
  const sheetQuery = useProductionOrderSheetQuery(id, { enabled: parsed.success });
  const order = orderQuery.data;
  const view = sheetQuery.data;
  const printed = useRef(false);

  const quoteId = order?.quoteRequestId ?? '';
  const quoteEnabled = parsed.success && order !== undefined;
  const detailQuery = useQuoteDetailQuery(quoteId, { enabled: quoteEnabled });
  const designViewQuery = useQuoteDesignViewQuery(quoteId, { enabled: quoteEnabled });
  const { refetch: refetchDesignView } = designViewQuery;
  const detail = detailQuery.data;
  const designView = designViewQuery.data;
  const readyView = designView?.status === 'READY' ? designView : undefined;
  const designBody = detail?.design.designBody ?? null;
  const facesMode = designBody !== null && readyView !== undefined;
  // 견적 상세·뷰 조회가 실패했거나 디자인을 그릴 수 없는(디자인 없는 일반 견적 포함) 견적이면 앞면 썸네일로 대신한다.
  // 썸네일도 없으면 기다릴 그림이 없어 바로 인쇄한다(needsWait).
  const thumbnailMode =
    (detail === undefined && detailQuery.isError) ||
    (designView === undefined && designViewQuery.isError) ||
    designView?.status === 'UNSUPPORTED' ||
    (detail !== undefined && designBody === null);
  const previewResolved = facesMode || thumbnailMode;

  // 서명 주소가 만료되면 면 위젯이 부른다 — 새 뷰를 다 받은 뒤 그 면을 다시 그린다.
  const refreshUrls = useCallback(async (): Promise<void> => {
    await refetchDesignView();
  }, [refetchDesignView]);

  const [settled, setSettled] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const markSettled = useCallback(() => setSettled(true), []);
  const ready = Boolean(order && view) && previewResolved;
  const needsWait = facesMode || order?.thumbnailUrl != null;
  const waitMs = facesMode ? FACES_WAIT_MS : THUMBNAIL_WAIT_MS;

  // 시안이 멈춰도 인쇄가 영영 막히지 않게 한다.
  useEffect(() => {
    if (!ready || !needsWait) return;
    const timer = setTimeout(() => setTimedOut(true), waitMs);
    return () => clearTimeout(timer);
  }, [ready, needsWait, waitMs]);

  useEffect(() => {
    if (ready && !printed.current && (!needsWait || settled || timedOut)) {
      printed.current = true;
      window.print();
    }
  }, [ready, needsWait, settled, timedOut]);

  // 주문 상세의 [인쇄 / PDF 저장]이 이 화면을 보이지 않는 프레임에 띄운다 — 인쇄창이 닫히면 알려 프레임을 거두게 한다.
  useEffect(() => {
    const onAfterPrint = (): void => {
      window.parent.postMessage({ type: PRODUCTION_ORDER_PRINTED_MESSAGE }, window.location.origin);
    };
    window.addEventListener('afterprint', onAfterPrint);
    return () => window.removeEventListener('afterprint', onAfterPrint);
  }, []);

  if (!parsed.success) {
    return <EmptyState isError>올바른 주문 주소가 아닙니다.</EmptyState>;
  }
  if (orderQuery.isPending || sheetQuery.isPending || (order && view && !previewResolved)) {
    return <EmptyState>불러오는 중…</EmptyState>;
  }
  if (!order || !view) {
    const notFound = orderErrorCode(orderQuery.error) === 'ORDER_NOT_FOUND';
    return (
      <EmptyState isError>
        {notFound ? '주문을 찾을 수 없습니다.' : '발주서를 불러오지 못했습니다.'}
      </EmptyState>
    );
  }

  return (
    <main className="min-h-screen bg-white p-6 text-black print:p-0">
      <div className="mb-4 flex justify-end gap-2 print:hidden">
        <Button type="button" variant="outline" size="sm" onClick={() => window.print()}>
          다시 인쇄
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => window.close()}>
          닫기
        </Button>
      </div>
      <ProductionOrderSheetDocument
        order={order}
        production={view.production}
        fields={view.sheet ?? view.defaults}
        issuedOn={koreanDate(new Date())}
        onThumbnailSettled={markSettled}
        designPreview={
          designBody !== null && readyView !== undefined ? (
            <ProductionOrderSheetFaces
              view={readyView}
              body={designBody}
              refreshUrls={refreshUrls}
              onAllSettled={markSettled}
            />
          ) : undefined
        }
      />
    </main>
  );
}
